/**
 * MenuItemBlock — pojedynczy element w canvasie buildera menu (ADR-0031).
 *
 * Renderowanie: ikona + label + drag handle + akcje (edit/delete/toggle collapse).
 * Per typ:
 *   - module / system_item / custom_link — single row, klikalne otwiera properties.
 *   - section — single row + lista dzieci pod spodem (rekurencyjny render).
 *   - separator — pozioma linia bez label.
 *
 * NIE odpowiada za drag&drop (to robi MenuCanvas + useSortable wrapper).
 * Czysto prezentacyjny + emituje callbacks.
 */

import { useTranslation } from 'react-i18next';
import {
  type AdminMenuItem,
  type AdminSystemItemDefinition,
  type MenuItem,
  SYSTEM_ITEMS,
  type SystemItemDefinition,
} from '@saas/shared';
import {
  ChevronDown,
  ChevronRight,
  Database,
  GripVertical,
  type LucideIcon,
  Trash2,
} from 'lucide-react';

type TFn = ReturnType<typeof useTranslation>['t'];

import { Badge } from '@/components/Badge';
import { Tooltip } from '@/components/Tooltip';
import { resolveIcon } from '@/lib/icon-map';

/**
 * Domyślna mapa system items dla tenant scope (back-compat).
 * Admin scope przekaże własną mapę z ADMIN_SYSTEM_ITEMS przez prop `systemItemDefinitions`.
 */
const DEFAULT_SYSTEM_BY_KEY: ReadonlyMap<string, SystemItemDefinition> = new Map(
  SYSTEM_ITEMS.map((s) => [s.itemKey as string, s]),
);

/**
 * Wspólny shape dla tenant + admin system item definicji — wystarcza dla MenuItemBlock
 * (potrzebne tylko `labelI18nKey` + `defaultLabel`). Path/iconName są opcjonalne tutaj
 * bo block ich nie używa.
 */
type AnySystemItemDefinition = Pick<SystemItemDefinition, 'labelI18nKey' | 'defaultLabel'> &
  Partial<Pick<SystemItemDefinition, 'iconName' | 'path'>>;

/**
 * Etykiety widoków pozycji menu (chip) — nazwa wybranego layoutu formularza i listy.
 * Liczone przez kontener (MenuCanvas) na podstawie viewRef/view + definicji encji,
 * podane gotowe (zlokalizowane) żeby MenuItemBlock pozostał prezentacyjny.
 */
export interface MenuItemViewLabels {
  form: string;
  list: string;
}

export interface MenuItemBlockProps {
  item: MenuItem | AdminMenuItem;
  isSelected: boolean;
  isCollapsed?: boolean;
  /** Chip etykiet layoutu formularza/listy (tylko pozycje type='module' w szablonie). */
  viewLabels?: MenuItemViewLabels;
  /**
   * Nazwa kartoteki (encji) do której odwołuje się pozycja menu type='module'.
   * Liczona przez kontener (MenuCanvas) z entities + templateCode. Gdy podana,
   * wiersz pokazuje accent-badge „kartoteka: …" widoczny też przy zwiniętym akordeonie.
   * Undefined (np. MenuBuilderShell bez entities) → brak badge.
   */
  kartotekaName?: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleCollapse?: (id: string) => void;
  /** Dla draggable wrapper — opaque listeners/attributes z useSortable. */
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  /**
   * i18n key prefix (np. 'tenant.menuLayoutBuilder' albo 'admin.menuLayoutBuilder').
   * Default: 'tenant.menuLayoutBuilder' (back-compat).
   */
  i18nPrefix?: string;
  /**
   * Mapa system item definicji (itemKey → definition) używana do fallback labelu
   * + ikony. Default: tenant SYSTEM_ITEMS. Admin scope przekazuje
   * ADMIN_SYSTEM_ITEMS map.
   */
  systemItemDefinitions?: ReadonlyMap<string, AnySystemItemDefinition | AdminSystemItemDefinition>;
  /**
   * Czy akordeon inline jest rozwinięty dla tej pozycji.
   * Gdy podane `onDeselect`, klik już zaznaczonej pozycji wywołuje onDeselect()
   * zamiast onSelect(id) — toggle do zwijania akordeonu.
   * Gdy onDeselect NIE podane, klik zaznaczonej pozycji wywołuje onSelect(id)
   * (zachowanie bez toggle — dla MenuBuilderShell).
   */
  isExpanded?: boolean;
  /**
   * Opcjonalny callback wywoływany gdy kliknięto już zaznaczoną pozycję
   * (toggle-deselect). Gdy niepodany, klik zawsze wywołuje onSelect(id).
   */
  onDeselect?: () => void;
  /**
   * Zawartość akordeonu (MenuItemInlineEditor) renderowana bezpośrednio pod wierszem.
   * Render decyduje kontener (MenuCanvas / SortableItem), nie MenuItemBlock.
   */
  inlineEditorSlot?: React.ReactNode;
}

export function MenuItemBlock({
  item,
  isSelected,
  isCollapsed,
  onSelect,
  onDelete,
  onToggleCollapse,
  dragHandleProps,
  viewLabels,
  kartotekaName,
  i18nPrefix = 'tenant.menuLayoutBuilder',
  systemItemDefinitions = DEFAULT_SYSTEM_BY_KEY,
  isExpanded = false,
  onDeselect,
  inlineEditorSlot,
}: MenuItemBlockProps) {
  const { t } = useTranslation();
  if (item.type === 'separator') {
    return (
      <div
        className={[
          'group flex items-center gap-2 rounded-md px-2 py-1.5 transition',
          isSelected ? 'bg-accent-50 ring-1 ring-accent-300' : 'hover:bg-bg-tertiary',
        ].join(' ')}
        data-testid={`menu-item-${item.id}`}
      >
        <button
          type="button"
          className="cursor-grab touch-none text-text-muted hover:text-text-primary"
          aria-label={t(`${i18nPrefix}.aria.drag`)}
          {...dragHandleProps}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="h-px flex-1 bg-border-secondary" />
        <span className="text-[10px] uppercase tracking-wider text-text-muted">
          {t(`${i18nPrefix}.badge.separator`)}
        </span>
        <button
          type="button"
          className="rounded p-1 text-text-muted opacity-0 transition group-hover:opacity-100 hover:bg-red-50 hover:text-red-600"
          aria-label={t(`${i18nPrefix}.aria.delete`)}
          onClick={() => onDelete(item.id)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  const Icon = resolveIcon(item.iconName, getDefaultIcon(item.type));
  const isSectionExpanded =
    item.type === 'section' ? !(isCollapsed ?? item.collapsed ?? false) : false;
  const displayLabel = item.label ?? getFallbackLabel(item, t, systemItemDefinitions, i18nPrefix);

  return (
    // Wrapper bez overflow-hidden — akordeon musi rosnąć pionowo.
    <div data-testid={`menu-item-${item.id}`} className="rounded-md transition">
      {/* Wiersz pozycji */}
      <div
        className={[
          'group flex items-center gap-2 rounded-md px-2 py-1.5 transition',
          isExpanded
            ? 'rounded-b-none bg-accent-50 ring-1 ring-accent-300'
            : isSelected
              ? 'bg-accent-50 ring-1 ring-accent-300'
              : 'hover:bg-bg-tertiary',
        ].join(' ')}
      >
        <button
          type="button"
          className="cursor-grab touch-none text-text-muted hover:text-text-primary"
          aria-label={t(`${i18nPrefix}.aria.drag`)}
          {...dragHandleProps}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {item.type === 'section' && onToggleCollapse ? (
          <button
            type="button"
            className="text-text-muted hover:text-text-primary"
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse(item.id);
            }}
            aria-label={t(
              isSectionExpanded
                ? `${i18nPrefix}.aria.collapseSection`
                : `${i18nPrefix}.aria.expandSection`,
            )}
          >
            {isSectionExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        ) : null}

        <Icon className="h-4 w-4 flex-shrink-0 text-text-secondary" />

        <button
          type="button"
          onClick={() => {
            // Toggle: klik zaznaczonego → onDeselect() jeśli dostępny (akordeon),
            // albo onSelect(item.id) jak zwykle (MenuBuilderShell — bez toggle).
            // Klik niezaznaczonego → zawsze onSelect(item.id).
            if (isSelected && onDeselect) {
              onDeselect();
            } else {
              onSelect(item.id);
            }
          }}
          className="flex-1 truncate text-left text-sm text-text-primary"
        >
          {displayLabel}
        </button>

        {/* Badge kartoteki — widoczny w wierszu (także przy zwiniętym akordeonie),
            żeby wiadomo było do której kartoteki odwołuje się pozycja bez rozwijania. */}
        {kartotekaName ? (
          <Badge
            color="accent"
            className="max-w-[160px] flex-shrink-0 text-[10px]"
            data-testid={`menu-item-kartoteka-${item.id}`}
          >
            <Database className="mr-1 h-3 w-3 flex-shrink-0" />
            <Tooltip content={kartotekaName}>
              <span className="truncate">
                {t('admin.menuItemViews.kartotekaBadge', {
                  name: kartotekaName,
                  defaultValue: `kartoteka: ${kartotekaName}`,
                })}
              </span>
            </Tooltip>
          </Badge>
        ) : null}

        {viewLabels ? (
          <div className="flex flex-shrink-0 items-center gap-1">
            <Tooltip content={viewLabels.form}>
              <span
                className="max-w-[140px] truncate rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-secondary"
                data-testid={`menu-item-chip-form-${item.id}`}
              >
                {viewLabels.form}
              </span>
            </Tooltip>
            <Tooltip content={viewLabels.list}>
              <span
                className="max-w-[140px] truncate rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-secondary"
                data-testid={`menu-item-chip-list-${item.id}`}
              >
                {viewLabels.list}
              </span>
            </Tooltip>
          </div>
        ) : null}

        {/* Badge typu — pomijamy dla pozycji 'module' (kartoteka): ikona + chipy
            Formularz/Lista wystarczają, słowo „MODUŁ" było zbędnym szumem. */}
        {item.type !== 'module' ? (
          <span className="text-[10px] uppercase tracking-wider text-text-muted">
            {t(`${i18nPrefix}.badge.${item.type}`)}
          </span>
        ) : null}

        {/* Akordeon-chevron — wskazuje stan rozwinięcia edytora inline.
            Oddzielny od sekcji-chevron (section-collapse) — ten dotyczy
            akordeonu właściwości, tamten — widoczności dzieci sekcji.
            Pokazywany gdy przekazano onDeselect (sygnał że akordeon jest podpięty). */}
        {onDeselect !== undefined ? (
          <button
            type="button"
            className="flex-shrink-0 rounded p-0.5 text-text-muted transition hover:text-text-primary"
            aria-label={t(
              isExpanded
                ? `${i18nPrefix}.aria.collapseAccordion`
                : `${i18nPrefix}.aria.expandAccordion`,
            )}
            onClick={() => {
              if (isExpanded && onDeselect) {
                onDeselect();
              } else {
                onSelect(item.id);
              }
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : null}

        <button
          type="button"
          className="rounded p-1 text-text-muted opacity-0 transition group-hover:opacity-100 hover:bg-red-50 hover:text-red-600"
          aria-label={t(`${i18nPrefix}.aria.delete`)}
          onClick={() => onDelete(item.id)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Akordeon inline — renderowany gdy isExpanded */}
      {isExpanded && inlineEditorSlot ? inlineEditorSlot : null}
    </div>
  );
}

function getDefaultIcon(type: MenuItem['type'] | AdminMenuItem['type']): LucideIcon {
  switch (type) {
    case 'module':
      return resolveIcon('Package');
    case 'system_item':
      return resolveIcon('Settings');
    case 'custom_link':
      return resolveIcon('Link');
    case 'section':
      return resolveIcon('Database');
    case 'separator':
      return resolveIcon('Minus');
    case 'installed_area':
      return resolveIcon('Boxes');
    default: {
      // Exhaustive check — gdy dorzucimy nowy MenuItemType w @saas/shared,
      // TS skompiluje fail tutaj. Runtime fallback zachowany na bezpieczeństwo.
      const _exhaustive: never = type;
      void _exhaustive;
      return resolveIcon('Package');
    }
  }
}

function getFallbackLabel(
  item: MenuItem | AdminMenuItem,
  t: TFn,
  systemItemDefinitions: ReadonlyMap<string, AnySystemItemDefinition | AdminSystemItemDefinition>,
  i18nPrefix: string,
): string {
  switch (item.type) {
    case 'module':
      // type='module' tylko w MenuItem (tenant). Admin scope nigdy tu nie wejdzie.
      return (item as MenuItem).moduleCode ?? t(`${i18nPrefix}.fallback.module`);
    case 'system_item': {
      const def = item.itemKey ? systemItemDefinitions.get(item.itemKey as string) : undefined;
      if (def) return t(def.labelI18nKey, def.defaultLabel);
      return item.itemKey ?? t(`${i18nPrefix}.fallback.system`);
    }
    case 'custom_link':
      return item.url ?? t(`${i18nPrefix}.fallback.customLink`);
    case 'installed_area':
      // type='installed_area' tylko w MenuItem (tenant) — analogicznie do 'module'.
      return (item as MenuItem).areaCode ?? t(`${i18nPrefix}.fallback.installedArea`);
    case 'section':
      return t(`${i18nPrefix}.fallback.section`);
    case 'separator':
      return '';
    default: {
      // Exhaustive check — patrz getDefaultIcon.
      const _exhaustive: never = item;
      void _exhaustive;
      return '';
    }
  }
}
